import { useState, useCallback } from "react";
import { useAuthStore } from "@/store/authStore";
import { trpc } from "@/trpc/client";
import { Copy, Plus, Truck, Users, Activity, PlusCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { Progress } from "@/components/ui/progress";
import AnalyticsDashboard from "@/components/analytics/AnalyticsDashboard";

const FleetDashboard = () => {
    const { user } = useAuthStore();
    // derived directly to ensure it updates if user loads late
    const inviteLink = `${window.location.origin}/register/driver?ref=${user?.id}`;

    // Fetch Stats
    const { data: stats } = trpc.user.getFleetStats.useQuery();
    // Fetch Drivers
    const { data: drivers } = trpc.user.getMyDrivers.useQuery();
    // Fetch Vehicles
    const { data: vehicles } = trpc.user.getMyVehicles.useQuery();

    const copyInvite = () => {
        navigator.clipboard.writeText(inviteLink);
        toast.success("Invite link copied!");
    };

    const [isRegisterOpen, setRegisterOpen] = useState(false);

    return (
        <div className="container mx-auto p-4 sm:p-6 space-y-6 sm:space-y-8 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
                        Fleet Command
                    </h1>
                    <p className="text-muted-foreground text-sm md:text-base">Manage your drivers, vehicles, and assignments.</p>
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
                <Card className="glass shadow-sm">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Active Loads</CardTitle>
                        <Activity className="h-4 w-4 text-primary" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{stats?.activeLoads || 0}</div>
                        <p className="text-xs text-muted-foreground">in transit</p>
                    </CardContent>
                </Card>
                <Card className="glass">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Drivers</CardTitle>
                        <Users className="h-4 w-4 text-primary" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{stats?.totalDrivers || 0}</div>
                        <p className="text-xs text-muted-foreground">active personnel</p>
                    </CardContent>
                </Card>
                <Card className="glass">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Vehicles</CardTitle>
                        <Truck className="h-4 w-4 text-primary" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{stats?.totalVehicles || 0}</div>
                        <p className="text-xs text-muted-foreground">ready to deploy</p>
                    </CardContent>
                </Card>
            </div>

            {/* Main Content */}
            <Tabs defaultValue="overview" className="space-y-4">
                <TabsList className="w-full justify-start overflow-x-auto h-auto p-1 flex-nowrap no-scrollbar">
                    <TabsTrigger value="overview" className="flex-shrink-0">Overview</TabsTrigger>
                    <TabsTrigger value="dispatch" className="flex-shrink-0">Dispatch</TabsTrigger>
                    <TabsTrigger value="drivers" className="flex-shrink-0">Drivers</TabsTrigger>
                    <TabsTrigger value="vehicles" className="flex-shrink-0">Vehicles</TabsTrigger>
                    <TabsTrigger value="analytics" className="flex-shrink-0">Analytics</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Recent Activity</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-muted-foreground">No recent activity.</p>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="dispatch" className="space-y-4">
                    <DispatchBoard />
                </TabsContent>

                <TabsContent value="drivers">
                    <div className="space-y-4">
                        <Card className="p-4 bg-muted/10">
                            <div className="space-y-3">
                                <div>
                                    <p className="font-semibold">Invite Drivers</p>
                                    <p className="text-sm text-muted-foreground">Share this link to add drivers to your fleet.</p>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <Input value={inviteLink} readOnly className="flex-1 text-sm" />
                                    <Button onClick={copyInvite} className="w-full sm:w-auto">
                                        <Copy className="w-4 h-4 mr-2" />
                                        Copy
                                    </Button>
                                </div>
                            </div>
                        </Card>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {drivers?.map(driver => (
                                <Card key={driver.id}>
                                    <CardHeader>
                                        <CardTitle className="text-lg">{driver.profile?.fullName || 'Pending Profile'}</CardTitle>
                                        <CardDescription>{driver.email}</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-sm text-muted-foreground">P: {driver.phoneOne || 'N/A'}</p>
                                        <div className="mt-2 text-xs bg-muted p-1 rounded inline-block">
                                            {driver.assignments?.[0] ? 'On Mission' : 'Idle'}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                            {drivers?.length === 0 && <p className="text-muted-foreground col-span-full text-center py-8">No drivers yet. Invite some!</p>}
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="vehicles">
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <div>
                                <h3 className="text-lg font-medium">Fleet Assets</h3>
                                <p className="text-sm text-muted-foreground">Manage your trucks and capacity.</p>
                            </div>
                            <Dialog open={isRegisterOpen} onOpenChange={setRegisterOpen}>
                                <DialogTrigger asChild>
                                    <Button className="gap-2">
                                        <PlusCircle className="w-4 h-4" />
                                        Register Vehicle(s)
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-2xl">
                                    <DialogHeader>
                                        <DialogTitle>Register Vehicles</DialogTitle>
                                        <DialogDescription>
                                            Add one or multiple vehicles to your fleet efficiently.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <VehicleRegistrationForm onSuccess={() => setRegisterOpen(false)} />
                                </DialogContent>
                            </Dialog>
                        </div>

                        {/* Company Verification Alert */}
                        {user?.verificationStatus === 'UNVERIFIED' && (
                            <div className="bg-yellow-500/10 border border-yellow-500/50 p-4 rounded-md flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Activity className="text-yellow-500 h-5 w-5" />
                                    <div>
                                        <h4 className="font-semibold text-yellow-500">Company Verification Required</h4>
                                        <p className="text-sm text-muted-foreground">You must verify your company before registering vehicles.</p>
                                    </div>
                                </div>
                                <VerifyCompanyButton />
                            </div>
                        )}

                        {/* Grouped Vehicle List */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {Object.entries(vehicles?.reduce((groups, vehicle) => {
                                const key = `${vehicle.make} ${vehicle.model}`;
                                if (!groups[key]) groups[key] = [];
                                groups[key].push(vehicle);
                                return groups;
                            }, {} as Record<string, typeof vehicles>) || {}).map(([name, group]) => (
                                <Card key={name} className="relative overflow-hidden">
                                    <div className="absolute top-0 right-0 p-2 bg-primary text-primary-foreground text-xs font-bold rounded-bl-lg">
                                        x{group.length} Units
                                    </div>
                                    <CardHeader>
                                        <CardTitle className="text-lg">{name}</CardTitle>
                                        <CardDescription>{group[0].capacity} {group[0].capacityUnit}</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-1">
                                            <p className="text-xs text-muted-foreground">Status: {group.every(v => v.verified) ? 'All Verified' : 'Pending Verification'}</p>
                                            <p className="text-xs text-muted-foreground truncate">
                                                IDs: {group.slice(0, 3).map(v => v.licensePlate || '...').join(', ')}
                                                {group.length > 3 && ` +${group.length - 3} more`}
                                            </p>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                            {vehicles?.length === 0 && <p className="text-muted-foreground col-span-full text-center py-8">No vehicles yet.</p>}
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="analytics">
                    <AnalyticsDashboard />
                </TabsContent>
            </Tabs>
        </div>
    );
};

const VerifyCompanyButton = () => {
    const utils = trpc.useContext();
    const { mutate: verify, isLoading } = trpc.user.verifyCompany.useMutation({
        onSuccess: () => {
            toast.success("Verification submitted!");
            window.location.reload(); // Refresh to update user session/state roughly
        }
    });

    return (
        <Button size="sm" variant="outline" onClick={() => verify({ documentPath: 'mock_business_reg.pdf' })} disabled={isLoading}>
            {isLoading ? 'Uploading...' : 'Upload Business Reg'}
        </Button>
    )
}

// --- Sub-Components ---

const DispatchBoard = () => {
    // We need to fetch ACTIVE bookings (jobs we moved). 
    // And filter specific to Fleet Manager if "getMyActiveBookings" supports it (it checks Transporter role, Fleet Manager is compatible usually)
    // Wait, getMyActiveBookings in booking.ts checks check `user.role !== 'TRANSPORTER'`. I need to fix that too!
    // I will assume I fixed it or use the `getFleetStats` derived data? No, I need the bookings.
    // I'll use `trpc.booking.getMyActiveBookings` and if it fails, I'll fix the backend.

    const { data: bookings } = trpc.booking.getMyActiveBookings.useQuery();
    const { data: drivers } = trpc.user.getMyDrivers.useQuery();
    const { data: vehicles } = trpc.user.getMyVehicles.useQuery();

    const utils = trpc.useContext();
    const { mutate: assign, isLoading } = trpc.booking.assignLoad.useMutation({
        onSuccess: () => {
            toast.success("Driver assigned successfully");
            utils.booking.getMyActiveBookings.invalidate();
            utils.user.getMyDrivers.invalidate();
            utils.user.getFleetStats.invalidate();
        },
        onError: (err) => toast.error(err.message)
    });

    const [selectedBooking, setSelected] = useState<any>(null);
    const [assignForm, setAssign] = useState({ driverId: '', vehicleId: '', count: 1 });

    const handleAssign = (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedBooking) return;
        assign({
            bookingId: selectedBooking.id,
            driverId: assignForm.driverId,
            vehicleId: assignForm.vehicleId || undefined,
            loadCount: assignForm.count
        });
        setSelected(null);
    };

    return (
        <div className="space-y-6">
            <h2 className="text-xl font-semibold">Live Operations</h2>
            <div className="grid gap-4">
                {bookings?.map(booking => {
                    // Calculate unassigned
                    // booking.assignments isn't included in getMyActiveBookings types by default?
                    // I need to update getMyActiveBookings to include assignments.
                    // Assuming it does or I'll fix that next.
                    const assignments = (booking as any).assignments || [];
                    const assigned = assignments.reduce((sum: number, a: any) => sum + a.assignedLoadCount, 0);
                    const remaining = booking.bookedLoadCount - assigned;

                    return (
                        <Card key={booking.id} className="border-l-4 border-l-primary shadow-sm">
                            <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between p-4">
                                <div>
                                    <div className="flex items-center justify-between md:block">
                                        <CardTitle className="text-base md:text-lg">{booking.job.title}</CardTitle>
                                        <div className="md:hidden text-lg font-bold">{remaining} / {booking.bookedLoadCount}</div>
                                    </div>
                                    <CardDescription className="text-xs md:text-sm mt-1">
                                        From: {booking.job.pickupDistrict} • To: {booking.job.deliveryDistrict}
                                    </CardDescription>
                                    {/* Contact Details (Fleet View - Prioritize Job Specifics) */}
                                    <div className="mt-2 text-xs grid grid-cols-2 gap-2">
                                        <div className="bg-blue-50/50 p-2 rounded border border-blue-100">
                                            <div className="uppercase text-[10px] font-bold text-blue-800 mb-1">Pickup Contact</div>
                                            <div className="font-medium text-foreground">{booking.job.pickupContactName || booking.job.client?.profile?.fullName || 'Valued Client'}</div>
                                            <a href={`tel:${booking.job.pickupContactPhone || booking.job.client?.phoneOne || booking.job.client?.profile?.phoneOne}`} className="font-medium text-blue-600 hover:underline">{booking.job.pickupContactPhone || booking.job.client?.phoneOne || booking.job.client?.profile?.phoneOne || 'N/A'}</a>
                                        </div>
                                        <div className="bg-green-50/50 p-2 rounded border border-green-100">
                                            <div className="uppercase text-[10px] font-bold text-green-800 mb-1">Delivery Contact</div>
                                            <div className="font-medium text-foreground">{booking.job.deliveryContactName || 'Consignee'}</div>
                                            <a href={`tel:${booking.job.deliveryContactPhone}`} className="font-medium text-green-600 hover:underline">{booking.job.deliveryContactPhone || 'N/A'}</a>
                                        </div>
                                    </div>
                                </div>
                                <div className="hidden md:block text-right">
                                    <div className="text-2xl font-bold">{remaining} / {booking.bookedLoadCount}</div>
                                    <div className="text-xs text-muted-foreground">Loads Unassigned</div>
                                </div>
                            </CardHeader>
                            <CardContent className="p-4 pt-0">
                                {remaining > 0 ? (
                                    <Dialog>
                                        <DialogTrigger asChild>
                                            <Button size="sm" className="w-full md:w-auto mt-2 md:mt-0" onClick={() => { setSelected(booking); setAssign({ ...assignForm, count: remaining }); }}>
                                                Assign Driver
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>Assign Mission</DialogTitle>
                                                <DialogDescription>Allocate loads to a driver.</DialogDescription>
                                            </DialogHeader>
                                            <form onSubmit={handleAssign} className="space-y-4">
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium">Driver</label>
                                                    <select className="w-full p-2 border rounded" required
                                                        onChange={e => setAssign({ ...assignForm, driverId: e.target.value })}>
                                                        <option value="">Select Pilot...</option>
                                                        {drivers?.filter(d => !d.assignments?.[0]).map(d => (
                                                            <option key={d.id} value={d.id}>{d.profile?.fullName} (Idle)</option>
                                                        ))}
                                                        {drivers?.filter(d => d.assignments?.[0]).map(d => (
                                                            <option key={d.id} value={d.id} disabled>{d.profile?.fullName} (Busy)</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium">Vehicle (Optional)</label>
                                                    <select className="w-full p-2 border rounded"
                                                        onChange={e => setAssign({ ...assignForm, vehicleId: e.target.value })}>
                                                        <option value="">Auto-Assign / N/A</option>
                                                        {vehicles?.map(v => (
                                                            <option key={v.id} value={v.id}>{v.make} {v.model} ({v.licensePlate || 'No Plate'})</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium">Load Count</label>
                                                    <Input type="number" min="1" max={remaining} value={assignForm.count}
                                                        onChange={e => setAssign({ ...assignForm, count: parseInt(e.target.value) })} />
                                                </div>
                                                <Button type="submit" disabled={isLoading} className="w-full">
                                                    {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                                    {isLoading ? "Assigning..." : "Confirm Assignment"}
                                                </Button>
                                            </form>
                                        </DialogContent>
                                    </Dialog>
                                ) : (
                                    <div className="text-sm text-green-600 font-medium flex items-center gap-2">
                                        <Activity className="w-4 h-4" /> Fully Assigned
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
                {bookings?.length === 0 && <p className="text-muted-foreground text-center py-8">No active jobs found. Go to 'Find Jobs' to secure work.</p>}
            </div>
        </div>
    );
};

// Consolidated VehicleRegistrationForm with real file upload
const VehicleRegistrationForm = ({ onSuccess }: { onSuccess?: () => void }) => {
    const { user, token } = useAuthStore();
    const isFleetManager = String(user?.role).toUpperCase() === 'FLEET_MANAGER';
    // Check if Fleet Manager needs to verify company (One-time requirement)
    const needsCompanyVerification = isFleetManager && user?.verificationStatus === 'UNVERIFIED';

    const utils = trpc.useContext();
    const { mutate: registerVehicle, isLoading: isRegistering } = trpc.vehicle.create.useMutation({
        onSuccess: () => {
            toast.success("Vehicles registered successfully");
            utils.user.getMyVehicles.invalidate();
            utils.user.getFleetStats.invalidate();
            onSuccess?.();
            if (needsCompanyVerification) {
                // Force reload/re-fetch profile to update verification status in UI
                window.location.reload();
            }
        },
        onError: (err) => {
            toast.error(err.message);
        }
    });

    const { mutateAsync: verifyCompany } = trpc.user.verifyCompany.useMutation();

    const [formState, setForm] = useState({
        make: '', model: '', licensePlate: '', capacity: 30, capacityUnit: 'TONNE' as const, count: 1
    });

    const [galleryImages, setGalleryImages] = useState<File[]>([]);
    const [registrationDoc, setRegistrationDoc] = useState<File | null>(null);
    const [ownershipDoc, setOwnershipDoc] = useState<File | null>(null);
    const [companyDoc, setCompanyDoc] = useState<File | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);

    // --- Upload Logic (Axios) ---
    const uploadFiles = async (files: File[], type: string): Promise<string[]> => {
        const formData = new FormData();
        files.forEach(f => formData.append('files', f));
        formData.append('type', type);

        const { data } = await axios.post(`${import.meta.env.VITE_API_URL || 'http://localhost:4000'}/upload`, formData, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percent);
                }
            }
        });
        return data.files.map((f: { filePath: string }) => f.filePath);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            // Validation: Company Doc required for Unverified Fleet Manager
            if (needsCompanyVerification && !companyDoc) {
                toast.error("Please upload your Company Registration Document to proceed.");
                return;
            }

            // Validation: Vehicle Docs required only if NOT Fleet Manager
            if (!isFleetManager && (!registrationDoc || !ownershipDoc)) {
                toast.error("Please upload registration and ownership documents.");
                return;
            }
            if (galleryImages.length === 0) {
                toast.error("Please upload at least one vehicle image.");
                return;
            }

            setUploadProgress(1);

            // 1. Handle Company Verification First (if needed)
            if (needsCompanyVerification && companyDoc) {
                const companyDocPaths = await uploadFiles([companyDoc], 'company_documents');
                if (companyDocPaths[0]) {
                    // Submit verification (Async) - wait for it to ensure it goes through
                    await verifyCompany({ documentPath: companyDocPaths[0] });
                }
            }

            // 2. Gallery
            const imagePaths = await uploadFiles(galleryImages, 'vehicle_gallery');

            // 3. Registration (Skip if Fleet)
            let regPaths: string[] = [];
            if (!isFleetManager && registrationDoc) {
                regPaths = await uploadFiles([registrationDoc], 'vehicle_documents');
            }

            // 4. Ownership (Skip if Fleet)
            let ownPaths: string[] = [];
            if (!isFleetManager && ownershipDoc) {
                ownPaths = await uploadFiles([ownershipDoc], 'vehicle_documents');
            }

            registerVehicle({
                ...formState,
                registrationDocPath: regPaths[0] || '', // Empty string allowed for Fleet Manager
                ownershipDocPath: ownPaths[0] || '',   // Empty string allowed for Fleet Manager
                imageFilePaths: imagePaths
            });

            setUploadProgress(0);
            setGalleryImages([]);
            setRegistrationDoc(null);
            setOwnershipDoc(null);
            setCompanyDoc(null);
        } catch (error: any) {
            console.error(error);
            toast.error(error.message || "Upload failed");
            setUploadProgress(0);
        }
    };

    // --- File Dropzone Helpers ---
    const onDropGallery = useCallback((acceptedFiles: File[]) => {
        setGalleryImages(acceptedFiles);
    }, []);

    const { getRootProps: getGalleryRoot, getInputProps: getGalleryInput, isDragActive: isGalleryDrag } = useDropzone({
        onDrop: onDropGallery,
        accept: { 'image/*': [] },
        maxFiles: 5
    });

    const onDropReg = useCallback((acceptedFiles: File[]) => { setRegistrationDoc(acceptedFiles[0]); }, []);
    const { getRootProps: getRegRoot, getInputProps: getRegInput } = useDropzone({
        onDrop: onDropReg, accept: { 'application/pdf': [], 'image/*': [] }, maxFiles: 1
    });

    const onDropOwn = useCallback((acceptedFiles: File[]) => { setOwnershipDoc(acceptedFiles[0]); }, []);
    const { getRootProps: getOwnRoot, getInputProps: getOwnInput } = useDropzone({
        onDrop: onDropOwn, accept: { 'application/pdf': [], 'image/*': [] }, maxFiles: 1
    });

    const onDropCompany = useCallback((acceptedFiles: File[]) => { setCompanyDoc(acceptedFiles[0]); }, []);
    const { getRootProps: getCompanyRoot, getInputProps: getCompanyInput } = useDropzone({
        onDrop: onDropCompany, accept: { 'application/pdf': [], 'image/*': [] }, maxFiles: 1
    });

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {/* ALERT: Company Verification Required */}
            {needsCompanyVerification && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <Activity className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-yellow-700">
                                First-time setup: Please upload your <strong>Company Registration</strong> document.
                                You only need to do this once.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <label className="text-sm font-medium">Make</label>
                    <Input placeholder="e.g. Scania" required
                        value={formState.make} onChange={e => setForm({ ...formState, make: e.target.value })} />
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium">Model</label>
                    <Input placeholder="e.g. R450" required
                        value={formState.model} onChange={e => setForm({ ...formState, model: e.target.value })} />
                </div>
            </div>

            <div className="space-y-2 p-4 bg-muted/20 rounded-lg border-2 border-dashed border-primary/20">
                <label className="text-sm font-bold text-primary">How many vehicles?</label>
                <div className="flex items-center gap-4">
                    <Input type="number" min="1" max="50" className="text-lg font-bold w-24"
                        value={formState.count} onChange={e => setForm({ ...formState, count: parseInt(e.target.value) })} />
                    <span className="text-sm text-muted-foreground">
                        {formState.count > 1 ? `entries will be created (e.g. ${formState.model} #1 - #${formState.count})` : 'single entry'}
                    </span>
                </div>
            </div>

            {formState.count === 1 && (
                <div className="space-y-2">
                    <label className="text-sm font-medium">License Plate (Optional)</label>
                    <Input placeholder="e.g. MZ 1234"
                        value={formState.licensePlate} onChange={e => setForm({ ...formState, licensePlate: e.target.value })} />
                </div>
            )}

            {/* Documents Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* 1. Company Registration (ONLY if Unverified Fleet Manager) */}
                {needsCompanyVerification && (
                    <div className="space-y-2 col-span-full md:col-span-2">
                        <label className="text-sm font-medium flex items-center gap-2">
                            Company Registration <span className="text-red-500 text-xs">(Required Once)</span>
                        </label>
                        <div {...getCompanyRoot()} className="border-2 border-dashed border-yellow-400/50 bg-yellow-50/50 rounded-md p-6 text-center cursor-pointer hover:bg-yellow-100/50 transition-colors">
                            <input {...getCompanyInput()} />
                            {companyDoc ? (
                                <div className="text-sm text-green-600 font-medium truncate flex items-center justify-center gap-2">
                                    <CheckCircle className="w-4 h-4" /> {companyDoc.name}
                                </div>
                            ) : (
                                <p className="text-sm text-muted-foreground">Upload Business Registration Certificate (PDF/Image)</p>
                            )}
                        </div>
                    </div>
                )}

                {/* 2. Vehicle Docs (ONLY if NOT Fleet Manager) */}
                {!isFleetManager && (
                    <>
                        {/* Registration Doc */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Registration Document</label>
                            <div {...getRegRoot()} className="border-2 border-dashed rounded-md p-4 text-center cursor-pointer hover:bg-muted/50 transition-colors">
                                <input {...getRegInput()} />
                                {registrationDoc ? (
                                    <div className="text-sm text-green-600 font-medium truncate">{registrationDoc.name}</div>
                                ) : (
                                    <p className="text-xs text-muted-foreground">Upload Registration (PDF/Image)</p>
                                )}
                            </div>
                        </div>

                        {/* Ownership Doc */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Proof of Ownership</label>
                            <div {...getOwnRoot()} className="border-2 border-dashed rounded-md p-4 text-center cursor-pointer hover:bg-muted/50 transition-colors">
                                <input {...getOwnInput()} />
                                {ownershipDoc ? (
                                    <div className="text-sm text-green-600 font-medium truncate">{ownershipDoc.name}</div>
                                ) : (
                                    <p className="text-xs text-muted-foreground">Upload Ownership (PDF/Image)</p>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Real Image Upload */}
            <div className="space-y-2">
                <label className="text-sm font-medium">Vehicle Images (Bulk Applied)</label>
                <div {...getGalleryRoot()} className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${isGalleryDrag ? 'border-primary bg-primary/10' : 'hover:bg-muted/50'}`}>
                    <input {...getGalleryInput()} />
                    <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
                        <Truck className="w-6 h-6 text-primary" />
                    </div>
                    {galleryImages.length > 0 ? (
                        <p className="text-sm font-medium text-green-600">{galleryImages.length} image(s) selected</p>
                    ) : (
                        <p className="text-sm font-medium">Drag & drop or click to upload images</p>
                    )}
                    <p className="text-xs text-muted-foreground">Applying to all {formState.count} vehicles</p>
                </div>
            </div>
            {uploadProgress > 0 && <Progress value={uploadProgress} className="h-2" />}

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <label className="text-sm font-medium">Capacity</label>
                    <Input type="number" min="0.1" step="0.1" required
                        value={formState.capacity} onChange={e => setForm({ ...formState, capacity: parseFloat(e.target.value) })} />
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium">Unit</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={formState.capacityUnit} onChange={e => setForm({ ...formState, capacityUnit: e.target.value as any })}>
                        <option value="TONNE">Tonne</option>
                        <option value="KG">Kg</option>
                    </select>
                </div>
            </div>
            <Button type="submit" className="w-full" disabled={isRegistering || uploadProgress > 0}>
                {(isRegistering || uploadProgress > 0) ? "Processing..." : `Register ${formState.count} Vehicle${formState.count > 1 ? 's' : ''}`}
            </Button>
        </form>
    );

};

export default FleetDashboard;
